import { ActionTree, GetterTree, Module, MutationTree } from "vuex";
import sleep from "@/utils/sleep";
import {
    initialState as commonInitialState,
    ISimulatorState as ICommonState,
    mutations as commonMutations,
    actions as commonActions,
    IVtkFile,
    SaveStatus,
    Status
} from "@/modules/simulator/store";
import api from "../api";

export interface IWaterGate {
    id: number;
    gap: number;
}

export interface IReservoirInfo {
    quota_downstream: number;
    quota_flux: number;
    water_gates: number[];
}

export interface ISimulatorState extends ICommonState {
    water_gates: IWaterGate[];
    quota_flux: number;
    quota_downstream: number;
    stageData: {
        quota_flux?: number;
        quota_downstream?: number;
        water_gates?: Map<number, IWaterGate>;
    };
}

const initialState: ISimulatorState = {
    ...commonInitialState,
    water_gates: [
        {
            id: 1,
            gap: 0.0
        }
    ],
    quota_flux: 0.0,
    quota_downstream: 0.0,
    stageData: {}
};

const mutations: MutationTree<ISimulatorState> = {
    ...commonMutations,
    UPDATE_DATA(
        state,
        data: {
            quota_flux?: number;
            quota_downstream?: number;
        }
    ): void {
        for (const [key, value] of Object.entries(data)) {
            state[key] = value;
        }
        state.is_valid_results = false;
    },
    UPDATE_WATER_GATES(state, waterGates: IWaterGate[]): void {
        state.water_gates = waterGates;
        state.is_valid_results = false;
    },
    UPDATE_STAGE_DATA(
        state,
        data: {
            quota_flux?: number;
            quota_downstream?: number;
        }
    ): void {
        for (const [key, value] of Object.entries(data)) {
            state.stageData[key] = value;
        }
    },
    UPDATE_STAGE_WATER_GATES(state, waterGates: IWaterGate[]): void {
        if (typeof state.stageData.water_gates === "undefined") {
            state.stageData.water_gates = new Map<number, IWaterGate>();
        }

        const { water_gates: waterGatesMap } = state.stageData;

        for (const waterGate of waterGates) {
            waterGatesMap.set(waterGate.id, waterGate);
        }
    },
    CLEAR_STAGE_DATA(state) {
        state.stageData = {};
    },
    INITIALIZE(
        state,
        data: {
            water_gates: IWaterGate[];
            quota_flux: number;
            quota_downstream: number;
            status: Status;
            log: string;
            results: Object | null;
            started_at: string | null;
            time_elapsed: number;
            is_valid_results: boolean;
            vtk_files: IVtkFile[];
        }
    ) {
        state.water_gates = data.water_gates;
        state.quota_flux = data.quota_flux;
        state.quota_downstream = data.quota_downstream;
        state.status = data.status;
        state.log = data.log;
        state.results = data.results;
        state.started_at = data.started_at;
        state.time_elapsed = data.time_elapsed;
        state.is_valid_results = data.is_valid_results;
        state.vtk_files = data.vtk_files;
    }
};

const actions: ActionTree<ISimulatorState, any> = {
    ...commonActions,
    async initialize({ state, dispatch }): Promise<void> {
        await dispatch("getData");
        if (state.status === Status.running) {
            dispatch("startStatusCheck");
        }
    },
    async getData({ commit }): Promise<void> {
        const { data } = await api.simulator.getData();
        commit(
            "INITIALIZE",
            data as {
                water_gates: IWaterGate[];
                quota_flux: number;
                quota_downstream: number;
                status: Status;
                log: string;
                results: Object | null;
                started_at: string | null;
                time_elapsed: number;
                is_valid_results: boolean;
                vtk_files: IVtkFile[];
            }
        );
    },
    async updateData(
        { commit, dispatch },
        data: {
            quota_flux?: number;
            quota_downstream?: number;
        }
    ): Promise<void> {
        commit("UPDATE_DATA", data);
        commit("UPDATE_STAGE_DATA", data);
        commit("UPDATE_SAVE_STATUS", SaveStatus.changed);
        await dispatch("updateSaveTimer");
    },
    async updateWaterGates(
        { commit, dispatch },
        data: {
            waterGates: IWaterGate[];
            changedWaterGates: IWaterGate[];
        }
    ): Promise<void> {
        commit("UPDATE_WATER_GATES", data.waterGates);
        commit("UPDATE_STAGE_WATER_GATES", data.changedWaterGates);
        commit("UPDATE_SAVE_STATUS", SaveStatus.changed);
        dispatch("updateSaveTimer");
    },
    updateSaveTimer({ state, commit }): void {
        commit("UPDATE_SAVE_TIMER", async () => {
            const data = {};

            for (const [key, value] of Object.entries(state.stageData)) {
                if (value instanceof Map) {
                    data[key] = [...value.values()];
                } else {
                    data[key] = value;
                }
            }

            commit("UPDATE_SAVE_STATUS", SaveStatus.saving);
            await api.simulator.postData(data);

            commit("CLEAR_STAGE_DATA");

            await sleep(1000);
            commit("UPDATE_SAVE_STATUS", SaveStatus.saved);
        });
    },
    async simulate({ dispatch }): Promise<void> {
        const promiseResponse = api.simulator.simulate();
        await dispatch("startSimulation", promiseResponse);
        promiseResponse.then(() => {
            dispatch("startStatusCheck");
        });
    },
    async stopSimulation(): Promise<void> {
        await api.simulator.stopSimulation();
    },
    startStatusCheck({ commit }): void {
        async function check() {
            const {
                data: {
                    status,
                    log,
                    results,
                    started_at,
                    time_elapsed,
                    vtk_files
                }
            } = await api.simulator.getStatus();

            if (status === Status.running) {
                setTimeout(check, 5000);
            } else {
                commit("UPDATE_STATUS", status);
                commit("UPDATE_LOG", log);
                commit("UPDATE_RESULTS", results);
                commit("UPDATE_STARTED_AT", started_at);
                commit("UPDATE_TIME_ELAPSED", time_elapsed);
                commit("UPDATE_VTK_FILES", vtk_files);
            }
        }

        check();
    },
    async updateFromReservorInfo(
        { commit, state },
        reservoirInfo: IReservoirInfo
    ): Promise<void> {
        const data: {
            water_gates?: IWaterGate[];
            quota_flux: number;
            quota_downstream: number;
        } = {
            quota_flux: reservoirInfo.quota_flux,
            quota_downstream: reservoirInfo.quota_downstream
        };

        commit("UPDATE_DATA", data);

        if (reservoirInfo.water_gates.length === state.water_gates.length) {
            const waterGates = state.water_gates.map((item, i) => ({
                ...item,
                gap: reservoirInfo.water_gates[i]
            }));

            commit("UPDATE_WATER_GATES", waterGates);

            data.water_gates = waterGates;
        }

        await api.simulator.postData(data);
    }
};

const getters: GetterTree<ISimulatorState, any> = {};

export const simulator: Module<ISimulatorState, any> = {
    namespaced: true,
    state: initialState,
    mutations,
    actions,
    getters
};
